import moment from "moment";
import { narrowOrg, withParents } from "./audienceTree";

/**
 * Pure helpers for the publisher form: default values, mapping a manage-shape item onto the
 * form, the client-side media checks, and building the multipart payload the server expects
 * (SPEC 6.3 / 18). Kept out of the container so the form file stays readable.
 */

export const DATETIME_FORMAT = "YYYY-MM-DD HH:mm";
export const DEFAULT_TIMEZONE = "Asia/Manila";
export const DEFAULT_LIMITS = { image_mb: 8, pdf_mb: 20, video_mb: 200 };

export const ORG_TYPES = ["division", "department"];
export const VIDEO_TYPES = ["video_file", "video_link"];
export const FILE_MEDIA_TYPES = ["image", "pdf", "video_file"];

export const isVideo = (mediaType) => VIDEO_TYPES.includes(mediaType);

// A form repeats per period (SPEC 6.3): "none" is for notices only.
export const PERIODS = [["none", "No repeat per period"], ["once", "Once"], ["quarter", "Every quarter"], ["year", "Every year"]];
export const FORM_PERIODS = PERIODS.filter(([v]) => v !== "none");
export const CHOICE_TYPES = ["single", "multi"];

// ---- table questions (SPEC 23.2): options = {columns: [{key, label, type}], max_rows}
export const TABLE_COLUMN_TYPES = ["text", "number", "date"];
export const TABLE_MAX_COLUMNS = 8;
export const TABLE_DEFAULT_ROWS = 10;
export const COLUMN_KEY_RE = /^[a-z][a-z0-9_]{0,29}$/;

// A column key made from its label: lower case, a-z0-9 and "_", starts with a letter, at most 30
// characters, unique among `taken` (a "_2", "_3" ... suffix when needed).
export const columnKey = (label, taken) => {
  let base = String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!/^[a-z]/.test(base)) base = "col" + (base ? "_" + base : "");
  base = base.slice(0, 30).replace(/_+$/, "");
  const used = taken || [];
  let key = base;
  for (let n = 2; used.includes(key); n++) {
    const suffix = "_" + n;
    key = base.slice(0, 30 - suffix.length) + suffix;
  }
  return key;
};

const emptyOrg = () => ({ division: {}, department: {} });

// The form's values for a brand-new notice.
export const defaultValues = (user) => ({
  kind: "notice",
  title: "",
  headline: "",
  content: "",
  thumbnail: null,
  on_link: false,
  link: "",
  cta_label: "",
  cta_url: "",
  display_popup: false,
  display_tab: true,
  media_type: "",
  media_file: null,
  media_url: "",
  media_country_id: "0",
  media_existing: null,   // edit only: the variant already stored {media_type, url}; null for a new item or none
  company_wide: false,
  countries: {},
  org: emptyOrg(),
  levels: {},
  hire_min: "",
  hire_max: "",
  conditions: {},
  timezone: (user && user.timezone_name) || DEFAULT_TIMEZONE,
  starts_at: moment().format(DATETIME_FORMAT),
  ends_at: moment().add(30, "days").format(DATETIME_FORMAT),
  repeat: false,
  repeat_from: "",
  repeat_to: "",
  period: "none",
  behaviour: "closable",
  reshow_rule: "next_login",
  escalate_after_days: "",
  priority: "100",
  require_ack: false,
  ack_text: "",
  confirm_before_submit: false,
  after_submit_url: "",
  must_watch: false,
  notify_bell: true,
  status: true,
  anonymous: false,
  per_person: false,          // kind = form: one document per named employee, raised by HR (SPEC 23.2)
  require_signature: false,   // kind = form: "I, {name}, agree" + Sign (SPEC 23.2 / 23.6)
  questions: [],   // kind = form: [{qtype, label, help_text, required, options}] in position order
  custom_key: "",  // kind = custom: a key of lookups.custom_forms (server BroadcastRequest.php:101 required_if kind,custom)
});

const str = (v) => (v === null || v === undefined ? "" : String(v));

// Maps the item's audience rules onto the form's pick maps.
export const audienceFromRules = (rules) => {
  const out = { countries: {}, org: emptyOrg(), levels: {}, hire_min: "", hire_max: "", conditions: {} };
  (rules || []).forEach((rule) => {
    const id = str(rule.ref_id);
    switch (rule.rule_type) {
      case "country":
        if (rule.mode === "include") out.countries[id] = true;
        break;
      case "division":
      case "department":
        out.org[rule.rule_type][id] = rule.mode;
        break;
      case "level":
        out.levels[id] = rule.mode;
        break;
      case "hire_days": {
        let window = {};
        try { window = JSON.parse(rule.value || "{}"); } catch (e) { window = {}; }
        out.hire_min = str(window.min);
        out.hire_max = str(window.max);
        break;
      }
      case "condition":
        out.conditions[rule.value] = true;
        break;
      default:
        break;
    }
  });
  return out;
};

// The item's current question version as the builder edits it, in position order (SPEC 19.2).
export const questionsFromItem = (item) => {
  const list = item && item.form && Array.isArray(item.form.questions) ? item.form.questions.slice() : [];
  return list
    .sort((a, b) => Number(a.position) - Number(b.position))
    .map((q) => {
      const out = {
        qtype: q.qtype,
        label: str(q.label),
        help_text: str(q.help_text),
        required: !!q.required,
        options: Array.isArray(q.options) ? q.options.slice() : [],
        filled_by: q.filled_by === "hr" ? "hr" : "employee",
      };
      if (q.qtype === "table") {
        const def = q.options && !Array.isArray(q.options) ? q.options : {};
        out.options = [];
        out.columns = (def.columns || []).map((c) => ({ key: str(c.key), label: str(c.label), type: c.type || "text" }));
        out.max_rows = str(def.max_rows || TABLE_DEFAULT_ROWS);
      }
      return out;
    });
};

// The form's values for an existing item (manage shape, SPEC 18).
export const valuesFromItem = (item, user, lookups, isHr) => {
  const audience = audienceFromRules(item.audience);
  if (lookups) audience.org = withParents(audience.org, lookups, isHr);
  const modes = Array.isArray(item.display_mode) ? item.display_mode : String(item.display_mode || "").split(",");
  const media = Array.isArray(item.media) ? item.media.find((m) => Number(m.country_id) === 0) || item.media[0] : null;
  const recurrence = item.recurrence || null;
  return {
    ...defaultValues(user),
    kind: item.kind || "notice",
    title: str(item.title),
    headline: str(item.headline),
    content: str(item.content_html),
    on_link: item.on_link == 1,
    link: str(item.link),
    cta_label: str(item.cta_label),
    cta_url: str(item.cta_url),
    display_popup: modes.includes("popup"),
    display_tab: modes.includes("tab"),
    media_type: media ? media.media_type : "",
    media_url: media && media.media_type === "video_link" ? str(media.url) : "",
    media_country_id: media ? str(media.country_id) : "0",
    media_existing: media ? { media_type: media.media_type, url: str(media.url) } : null,
    ...audience,
    company_wide: false, // an item with no rules is company-wide, but the form shows its rules as they are
    timezone: item.timezone || DEFAULT_TIMEZONE,
    starts_at: str(item.starts_at_local),
    ends_at: str(item.ends_at_local),
    repeat: !!recurrence,
    repeat_from: recurrence ? str(recurrence.from) : "",
    repeat_to: recurrence ? str(recurrence.to) : "",
    period: item.period || "none",
    behaviour: item.behaviour || "closable",
    reshow_rule: item.reshow_rule || "next_login",
    escalate_after_days: str(item.escalate_after_days),
    priority: str(item.priority === null || item.priority === undefined ? 100 : item.priority),
    require_ack: !!item.require_ack,
    ack_text: str(item.ack_text),
    confirm_before_submit: !!item.confirm_before_submit,
    after_submit_url: str(item.after_submit_url),
    must_watch: !!item.must_watch,
    notify_bell: item.notify_bell === undefined ? true : !!item.notify_bell,
    status: item.status === undefined ? true : item.status == 1,
    anonymous: !!item.anonymous,
    per_person: !!Number(item.per_person || 0),
    require_signature: !!Number(item.require_signature || 0),
    questions: questionsFromItem(item),
    custom_key: str(item.custom_key),   // manage shape `custom_key` (server BroadcastPresenter.php:105)
  };
};

// ---- question checks (before anything is sent; the server repeats them, SPEC 19.1)

export const checkQuestions = (values) => {
  if (values.kind !== "form") return null;
  const list = Array.isArray(values.questions) ? values.questions : [];
  if (list.length === 0) return "Add at least one question";
  if (list.some((q) => !String(q.label || "").trim())) return "Every question needs a label";
  const badChoice = list.some((q) => CHOICE_TYPES.includes(q.qtype) && ((q.options || []).length < 2 || (q.options || []).length > 20));
  if (badChoice) return "Single and multiple choice need 2 to 20 options";
  const tables = list.filter((q) => q.qtype === "table");
  if (tables.some((q) => !(q.columns || []).length || q.columns.length > TABLE_MAX_COLUMNS)) return "A table needs 1 to 8 columns";
  if (tables.some((q) => q.columns.some((c) => !String(c.label || "").trim()))) return "Every table column needs a label";
  const rowsBad = (q) => { const n = Number(q.max_rows === "" || q.max_rows === undefined ? TABLE_DEFAULT_ROWS : q.max_rows); return !Number.isInteger(n) || n < 1 || n > 20; };
  if (tables.some(rowsBad)) return "Max rows must be between 1 and 20";
  return null;
};

// A custom item needs its registry key (the server answers 422 "A custom item needs a custom form key.",
// BroadcastRequest.php:101,132); content is optional for custom (only a notice needs content, :161).
export const CUSTOM_KEY_REQUIRED = "Choose a custom form";
export const checkCustomKey = (values) => (values.kind === "custom" && !values.custom_key ? CUSTOM_KEY_REQUIRED : null);

// ---- media checks (before anything is sent; the server repeats them and answers 422)

const MB = 1024 * 1024;
const ext = (name) => String(name || "").toLowerCase().split(".").pop();

const ACCEPT = {
  image: { mimes: ["image/jpeg", "image/png", "image/webp"], exts: ["jpg", "jpeg", "png", "webp"] },
  pdf: { mimes: ["application/pdf"], exts: ["pdf"] },
  video_file: { mimes: ["video/mp4", "video/webm"], exts: ["mp4", "webm"] },
};

const typeOk = (kind, file) => ACCEPT[kind].mimes.includes(file.type) || ACCEPT[kind].exts.includes(ext(file.name));

// Returns a message when the file is refused, otherwise null.
export const checkMediaFile = (mediaType, file, limits) => {
  if (!file) return null;
  const lim = { ...DEFAULT_LIMITS, ...(limits || {}) };
  switch (mediaType) {
    case "image":
      return typeOk("image", file) && file.size <= lim.image_mb * MB
        ? null : "Image must be JPG, PNG or WebP and at most " + lim.image_mb + " MB";
    case "pdf":
      return typeOk("pdf", file) && file.size <= lim.pdf_mb * MB
        ? null : "PDF must be at most " + lim.pdf_mb + " MB";
    case "video_file":
      return typeOk("video_file", file) && file.size <= lim.video_mb * MB
        ? null : "Video must be MP4 or WebM and at most " + lim.video_mb + " MB";
    default:
      return null;
  }
};

// ---- audience rules as the server expects them

// With the lookups, a parent org include is dropped when a narrower include sits under it
// (audienceTree.narrowOrg): the engine ORs org includes, the screen narrows.
export const rulesFromValues = (values, lookups) => {
  if (values.company_wide) return [];
  const org = lookups ? narrowOrg(values.org, lookups) : values.org;
  const rules = [];
  Object.keys(values.countries || {}).forEach((id) => {
    if (values.countries[id]) rules.push({ rule_type: "country", mode: "include", ref_id: id });
  });
  ORG_TYPES.forEach((type) => {
    Object.keys(org[type] || {}).forEach((id) => {
      const mode = org[type][id];
      if (mode) rules.push({ rule_type: type, mode: mode, ref_id: id });
    });
  });
  Object.keys(values.levels || {}).forEach((id) => {
    const mode = values.levels[id];
    if (mode) rules.push({ rule_type: "level", mode: mode, ref_id: id });
  });
  if (values.hire_min !== "" || values.hire_max !== "") {
    rules.push({
      rule_type: "hire_days", mode: "include",
      value: JSON.stringify({
        min: values.hire_min === "" ? 0 : Number(values.hire_min),
        max: values.hire_max === "" ? null : Number(values.hire_max),
      }),
    });
  }
  Object.keys(values.conditions || {}).forEach((key) => {
    if (values.conditions[key]) rules.push({ rule_type: "condition", mode: "include", value: key });
  });
  return rules;
};

// ---- the multipart payload (booleans as "1"/"0", arrays as name[] / name[i][field])

const bool = (v) => (v ? "1" : "0");

// True when the publisher chose a new file, or typed a video URL that is not the stored one.
export const mediaChanged = (values) => {
  if (!values.media_type) return false;
  if (values.media_type === "video_link") {
    const url = String(values.media_url || "").trim();
    if (!url) return false;
    const existing = values.media_existing;
    return !(existing && existing.media_type === "video_link" && existing.url === url);
  }
  return !!values.media_file;
};

// Label for the "Current media" line on the edit form.
export const MEDIA_LABELS = { image: "Image", pdf: "PDF", video_file: "Video file", video_link: "Video link" };

export const buildFormData = (values, lookups) => {
  const fd = new FormData();
  const video = isVideo(values.media_type);
  const mandatory = values.behaviour === "mandatory";

  fd.append("kind", values.kind);
  fd.append("title", values.title);
  fd.append("headline", values.headline);
  fd.append("content", values.content || "");
  if (values.display_popup) fd.append("display_mode[]", "popup");
  if (values.display_tab) fd.append("display_mode[]", "tab");
  fd.append("timezone", values.timezone);
  fd.append("starts_at", values.starts_at);
  fd.append("ends_at", values.ends_at);
  if (values.repeat) {
    fd.append("recurrence[type]", "monthly_days");
    fd.append("recurrence[from]", values.repeat_from);
    fd.append("recurrence[to]", values.repeat_to);
  }
  // SPEC 23.2: a per-person form is period "once", never anonymous; a signed form is never anonymous.
  const perPerson = values.kind === "form" && !!values.per_person;
  fd.append("period", perPerson ? "once" : values.period);
  fd.append("behaviour", values.behaviour);
  if (!mandatory) {
    fd.append("reshow_rule", values.reshow_rule);
    if (values.escalate_after_days !== "") fd.append("escalate_after_days", values.escalate_after_days);
  }
  if (values.priority !== "") fd.append("priority", values.priority);
  fd.append("require_ack", bool(values.require_ack));
  fd.append("ack_text", values.ack_text);
  fd.append("confirm_before_submit", bool(values.confirm_before_submit));
  fd.append("after_submit_url", values.after_submit_url);
  fd.append("cta_label", values.cta_label);
  fd.append("cta_url", values.cta_url);
  fd.append("must_watch", bool(video && values.must_watch));
  fd.append("notify_bell", bool(values.notify_bell));
  fd.append("on_link", bool(values.on_link));
  fd.append("link", values.link);
  fd.append("status", bool(values.status));
  fd.append("anonymous", bool(values.kind === "form" && values.anonymous && !perPerson && !values.require_signature));
  if (values.kind === "custom") fd.append("custom_key", values.custom_key || "");
  // SPEC 23.2: form only (on a notice / custom either flag is a 422, so they are never sent there).
  if (values.kind === "form") {
    fd.append("per_person", bool(values.per_person));
    fd.append("require_signature", bool(values.require_signature));
  }
  if (values.thumbnail) fd.append("thumbnail", values.thumbnail);

  // Position = array order (19.1): no position or id is ever sent, the whole list every time.
  if (values.kind === "form") {
    (values.questions || []).forEach((q, i) => {
      const key = "questions[" + i + "]";
      fd.append(key + "[qtype]", q.qtype);
      fd.append(key + "[label]", q.label || "");
      fd.append(key + "[help_text]", q.help_text || "");
      fd.append(key + "[required]", bool(q.required));
      // SPEC 23.2: `hr` only on a per-person form; everything else is filled in by the employee.
      fd.append(key + "[filled_by]", values.per_person && q.filled_by === "hr" ? "hr" : "employee");
      if (CHOICE_TYPES.includes(q.qtype)) {
        (q.options || []).forEach((option) => fd.append(key + "[options][]", option));
      }
      if (q.qtype === "table") {
        (q.columns || []).forEach((c, j) => {
          const col = key + "[columns][" + j + "]";
          fd.append(col + "[key]", c.key);
          fd.append(col + "[label]", c.label);
          fd.append(col + "[type]", c.type || "text");
        });
        fd.append(key + "[max_rows]", String(q.max_rows === "" || q.max_rows === undefined ? TABLE_DEFAULT_ROWS : q.max_rows));
      }
    });
  }

  // Media fields go out ONLY when there is something new to store: a file the publisher chose, or a
  // video link whose URL differs from the stored one. Re-sending media_type for an item that already
  // has an upload (and no new file) makes the server answer 422 "A file is required for this media
  // type"; sending nothing keeps the stored variant as it is.
  if (mediaChanged(values)) {
    fd.append("media_type", values.media_type);
    fd.append("media_country_id", values.media_country_id || "0");
    if (values.media_type === "video_link") {
      fd.append("media_url", values.media_url);
    } else {
      fd.append("media", values.media_file);
    }
  }

  rulesFromValues(values, lookups).forEach((rule, i) => {
    fd.append("audience[" + i + "][rule_type]", rule.rule_type);
    fd.append("audience[" + i + "][mode]", rule.mode);
    if (rule.ref_id !== undefined) fd.append("audience[" + i + "][ref_id]", rule.ref_id);
    if (rule.value !== undefined) fd.append("audience[" + i + "][value]", rule.value);
  });

  return fd;
};

// The due-shape item BroadcastModal renders for "Render preview" (no events are posted).
export const draftItem = (values, item) => {
  const video = isVideo(values.media_type);
  let media = null;
  if (values.media_type === "video_link" && values.media_url) {
    media = { media_type: "video_link", url: values.media_url, must_watch: values.must_watch };
  } else if (item && Array.isArray(item.media) && item.media.length && !values.media_file) {
    const existing = item.media.find((m) => Number(m.country_id) === 0) || item.media[0];
    media = { media_type: existing.media_type, url: existing.url, must_watch: video && values.must_watch };
  } else if (values.media_file && typeof URL !== "undefined" && URL.createObjectURL) {
    media = { media_type: values.media_type, url: URL.createObjectURL(values.media_file), must_watch: video && values.must_watch };
  }
  return {
    id: item ? item.id : 0,
    kind: values.kind,
    title: values.title,
    headline: values.headline || null,
    category: "preview",
    content_html: values.content || "",
    media: media,
    behaviour: values.behaviour,
    reshow_rule: values.reshow_rule,
    escalated: false,
    require_ack: !!values.require_ack,
    ack_text: values.ack_text || null,
    confirm_before_submit: !!values.confirm_before_submit,
    after_submit_url: values.after_submit_url || null,
    cta: values.cta_url ? { label: values.cta_label, url: values.cta_url } : null,
    period_key: "",
    form: values.kind === "form" ? draftForm(values, item) : null,
    custom_key: values.kind === "custom" ? values.custom_key || null : null,
    payload: values.kind === "custom" ? {} : null,
    anonymous: values.kind === "form" && !!values.anonymous,
    per_person: values.kind === "form" && !!values.per_person,
    require_signature: values.kind === "form" && !!values.require_signature,
    assignments: values.kind === "form" && values.per_person ? [] : undefined,
    state: { shown_count: 0, acknowledged_at: null, media_watched_at: null },
  };
};

// The due-shape form block for the preview; ids are positions since the draft has none yet.
const draftForm = (values, item) => ({
  version: item && item.form ? item.form.version : 1,
  questions: (values.questions || []).map((q, i) => ({
    id: i + 1,
    position: i + 1,
    qtype: q.qtype,
    label: q.label,
    help_text: q.help_text || null,
    required: !!q.required,
    options: draftOptions(q),
    filled_by: values.per_person && q.filled_by === "hr" ? "hr" : "employee",
  })),
});

const draftOptions = (q) => {
  if (q.qtype === "rating") return { min: 1, max: 5 };
  if (CHOICE_TYPES.includes(q.qtype)) return q.options;
  if (q.qtype === "table") return { columns: q.columns || [], max_rows: Number(q.max_rows || TABLE_DEFAULT_ROWS) };
  return null;
};

// Server validation messages arrive as an array (422) or a single string (403 etc.).
export const errorMessages = (e) => {
  const message = e && e.data && e.data.error ? e.data.error.message : null;
  if (Array.isArray(message)) return message;
  if (typeof message === "string" && message) return [message];
  return ["The announcement could not be saved. Please try again."];
};
